// =================================================================================================
// 🔑 FORGOT PASSWORD
// =================================================================================================

import React, { useState } from 'react';
import { Check, ArrowLeft, Loader, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ForgotPasswordPage({ API_URL }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [step, setStep] = useState(1); // 1: Saisie Email, 2: Confirmation envoi
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSendLink = async () => {
    if(!email) return;
    setIsLoading(true);
    setError(null);
    
    try {
      // Appel vers backend Node.js (server.js)
      const response = await fetch(`${API_URL}/auth/forgot-password`, {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify({ email })
      });
      
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Erreur lors de l'envoi");
      }
      
      // message de succès
      setStep(2);

    } catch(err) {
       // En production, pour des raisons de sécurité, on évite parfois de dire 
       // explicitement si l'email n'existe pas, mais ici on affiche l'erreur pour le debug.
       setError(err.message || "Une erreur est survenue. Veuillez réessayer.");
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 font-sans text-slate-900">
       <div className="bg-white p-8 rounded-2xl shadow-lg w-full max-w-md border border-gray-200">
          
          {/* Bouton Retour */}
          <button 
            onClick={() => navigate('/')} 
            className="mb-6 text-gray-400 hover:text-blue-600 flex items-center gap-2 text-sm transition-colors font-medium"
          >
             <ArrowLeft className="w-4 h-4" /> Retour à la connexion
          </button>

          <h2 className="text-2xl font-bold mb-2 text-slate-900">
            {step === 1 ? 'Mot de passe oublié ?' : 'Vérifiez vos emails'}
          </h2>
          
          {/* Affichage des erreurs */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2 border border-red-100 animate-in fade-in slide-in-from-top-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0"/> {error}
            </div>
          )}

          {step === 1 ? (
            <>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                Entrez votre adresse email professionnelle ci-dessous. Nous vous enverrons un lien sécurisé pour réinitialiser votre mot de passe.
              </p>
              
              <div className="space-y-4">
                  <input 
                    type="email" 
                    placeholder="jean@treprise.fr" 
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                    value={email} 
                    onChange={e => setEmail(e.target.value)} 
                    onKeyDown={(e) => e.key === 'Enter' && handleSendLink()}
                  />
                  <button 
                    onClick={handleSendLink} 
                    disabled={isLoading || !email}
                    className="w-full bg-slate-900 text-white py-3 rounded-lg font-bold hover:bg-slate-800 transition-all disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center shadow-sm hover:shadow-md"
                  >
                    {isLoading ? <Loader className="animate-spin w-5 h-5" /> : 'Envoyer le lien de réinitialisation'}
                  </button>
              </div>
            </>
          ) : (
            <div className="text-center animate-in fade-in zoom-in duration-300 py-4">
               <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                  <Check className="w-8 h-8 text-emerald-600" />
               </div>
               <p className="text-gray-600 mb-8 leading-relaxed">
                 Si un compte existe avec l'adresse <strong>{email}</strong>, vous allez recevoir un email contenant les instructions dans quelques instants.
               </p>
               <button 
                 onClick={() => navigate('/')} 
                 className="w-full py-3 rounded-lg border border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition-colors"
               >
                 Retour à la connexion
               </button>
            </div>
          )}
       </div>
    </div>
  );
}